import React from 'react';
import { ArrowLeft, Globe, Cloud, Shield, Zap, CheckCircle, Star, ArrowRight, Users, Database, Smartphone, BarChart3 } from 'lucide-react';

interface WebAppsPageProps {
  onBack: () => void;
}

const WebAppsPage: React.FC<WebAppsPageProps> = ({ onBack }) => {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const features = [
    {
      icon: Cloud,
      title: "Escalabilidad Automática",
      description: "Arquitectura cloud-native que se adapta automáticamente al crecimiento de tu negocio."
    },
    {
      icon: Shield,
      title: "Seguridad Enterprise",
      description: "Protección avanzada con encriptación, autenticación multi-factor y auditorías de seguridad."
    },
    {
      icon: Zap,
      title: "Alto Rendimiento",
      description: "Optimización avanzada para cargas masivas de usuarios y procesamiento de datos en tiempo real."
    },
    {
      icon: Smartphone,
      title: "Responsive Design",
      description: "Experiencia perfecta en cualquier dispositivo, desde móviles hasta pantallas 4K."
    }
  ];

  const technologies = [
    {
      name: "React/Next.js",
      description: "Frontend moderno con server-side rendering",
      logo: "⚛️"
    },
    {
      name: "Node.js/Express",
      description: "Backend escalable y de alto rendimiento",
      logo: "🟢"
    },
    {
      name: "PostgreSQL/MongoDB",
      description: "Bases de datos robustas para cualquier escala",
      logo: "🗄️"
    },
    {
      name: "AWS/Azure/GCP",
      description: "Infraestructura cloud de clase mundial",
      logo: "☁️"
    },
    {
      name: "Docker/Kubernetes",
      description: "Contenedores para máxima escalabilidad",
      logo: "🐳"
    },
    {
      name: "Redis/ElasticSearch",
      description: "Cache y búsqueda de alto rendimiento",
      logo: "🔍"
    }
  ];

  
  const architecture = [
    {
      layer: "Frontend",
      technologies: ["React", "Next.js", "TypeScript", "Tailwind CSS"],
      description: "Interfaces modernas y responsivas con experiencia de usuario excepcional"
    },
    {
      layer: "Backend",
      technologies: ["Node.js", "Express", "GraphQL", "REST APIs"],
      description: "APIs robustas y escalables con arquitectura de microservicios"
    },
    {
      layer: "Base de Datos",
      technologies: ["PostgreSQL", "MongoDB", "Redis", "ElasticSearch"],
      description: "Almacenamiento optimizado para diferentes tipos de datos y consultas"
    },
    {
      layer: "Infraestructura",
      technologies: ["AWS", "Docker", "Kubernetes", "CDN"],
      description: "Despliegue cloud con alta disponibilidad y escalabilidad automática"
    }
  ];

  const process = [
    {
      step: "01",
      title: "Arquitectura y Diseño",
      description: "Definimos la arquitectura técnica, stack tecnológico y diseño de la experiencia de usuario."
    },
    {
      step: "02",
      title: "Desarrollo MVP",
      description: "Creamos un producto mínimo viable para validar funcionalidades core con usuarios reales."
    },
    {
      step: "03",
      title: "Desarrollo Iterativo",
      description: "Implementamos funcionalidades en sprints con entregas continuas y feedback constante."
    },
    {
      step: "04",
      title: "Testing y QA",
      description: "Pruebas exhaustivas de rendimiento, seguridad y usabilidad en múltiples escenarios."
    },
    {
      step: "05",
      title: "Despliegue y Escalado",
      description: "Lanzamiento en producción con monitoreo continuo y escalado según demanda."
    }
  ];

  const scalabilityFeatures = [
    { feature: "Load Balancing", description: "Distribución automática de carga" },
    { feature: "Auto Scaling", description: "Escalado automático de recursos" },
    { feature: "CDN Global", description: "Entrega de contenido optimizada" },
    { feature: "Database Sharding", description: "Particionamiento de datos" },
    { feature: "Microservices", description: "Arquitectura de servicios independientes" },
    { feature: "Caching Layers", description: "Múltiples niveles de cache" }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="flex items-center space-x-2 text-gray-600 hover:text-blue-600 transition-colors duration-200"
            >
              <ArrowLeft className="h-5 w-5" />
              <span>Volver a Servicios</span>
            </button>
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-1">
                <img
                  src="/src/assets/logoModelos2.png"
                  alt="ModeloSmart Logo"
                  style={{ height: "120px", width: "auto" }}
                />
              </div>{" "}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-teal-50 via-white to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-6">
                <div className="flex items-center space-x-3">
                  <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center">
                    <Globe className="h-8 w-8 text-teal-600" />
                  </div>
                  <h1 className="text-5xl font-bold text-gray-900">
                    Aplicaciones Web <span className="text-teal-600">Escalables</span>
                  </h1>
                </div>
                <p className="text-xl text-gray-600 leading-relaxed">
                  Desarrollamos plataformas web robustas que crecen con tu negocio, manejan 
                  miles de usuarios concurrentes y se adaptan a cualquier demanda operacional.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => scrollToSection('contacto')}
                  className="inline-flex items-center px-8 py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-all duration-200 transform hover:scale-105 shadow-lg"
                >
                  Solicitar Cotización
                  <ArrowRight className="ml-2 h-5 w-5" />
                </button>

              </div>

              {/* Key Metrics */}
              <div className="grid grid-cols-3 gap-6 pt-4">
                <div className="text-center p-4 bg-teal-50 rounded-xl">
                  <div className="text-2xl font-bold text-teal-600 mb-1">99.9%</div>
                  <div className="text-sm text-gray-600">Uptime garantizado</div>
                </div>
                <div className="text-center p-4 bg-teal-50 rounded-xl">
                  <div className="text-2xl font-bold text-teal-600 mb-1">10K+</div>
                  <div className="text-sm text-gray-600">Usuarios concurrentes</div>
                </div>
                <div className="text-center p-4 bg-teal-50 rounded-xl">
                  <div className="text-2xl font-bold text-teal-600 mb-1">&lt;2s</div>
                  <div className="text-sm text-gray-600">Tiempo de carga</div>
                </div>
              </div>
            </div>

            {/* Hero Image */}
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/3861958/pexels-photo-3861958.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Desarrollo de aplicaciones web escalables"
                className="w-full h-96 object-cover rounded-2xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-teal-600/20 to-transparent rounded-2xl"></div>
              
              {/* Floating elements */}
              <div className="absolute -top-6 -right-6 bg-white p-4 rounded-xl shadow-lg">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-sm font-medium">Sistema en línea</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              ¿Por qué elegir nuestras <span className="text-teal-600">aplicaciones web</span>?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Construimos plataformas web con arquitectura enterprise que garantizan 
              rendimiento, seguridad y escalabilidad para cualquier tamaño de negocio.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-gray-50 p-8 rounded-2xl hover:shadow-lg transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className="w-16 h-16 bg-teal-100 rounded-xl flex items-center justify-center mb-6">
                  <feature.icon className="h-8 w-8 text-teal-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Architecture */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Arquitectura <span className="text-teal-600">Moderna</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Utilizamos las mejores prácticas de arquitectura de software para crear 
              aplicaciones web que escalan eficientemente y se mantienen fácilmente.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {architecture.map((layer, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <h3 className="text-xl font-bold text-gray-900 mb-4">{layer.layer}</h3>
                <div className="space-y-2 mb-4">
                  {layer.technologies.map((tech, idx) => (
                    <span
                      key={idx}
                      className="inline-block bg-teal-100 text-teal-700 px-3 py-1 rounded-full text-sm mr-2 mb-2"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
                <p className="text-gray-600 text-sm">{layer.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Scalability Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Características de <span className="text-teal-600">Escalabilidad</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Implementamos tecnologías avanzadas que permiten a tu aplicación crecer 
              sin límites y mantener rendimiento óptimo bajo cualquier carga.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {scalabilityFeatures.map((item, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-teal-50 to-blue-50 p-6 rounded-xl border border-teal-100 hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                    <Zap className="h-5 w-5 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">{item.feature}</h3>
                    <p className="text-sm text-gray-600">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      

      {/* Process */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Nuestro <span className="text-teal-600">Proceso</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Metodología ágil especializada en desarrollo de aplicaciones web escalables 
              con entregas continuas y feedback constante.
            </p>
          </div>

          <div className="relative">
            {/* Process line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-teal-600 to-blue-600 hidden lg:block"></div>
            
            <div className="space-y-12">
              {process.map((step, index) => (
                <div key={index} className="relative flex items-start">
                  <div className="hidden lg:flex w-16 h-16 bg-teal-600 rounded-full items-center justify-center text-white font-bold text-lg mr-8 flex-shrink-0 shadow-lg">
                    {step.step}
                  </div>
                  
                  <div className="bg-gray-50 p-8 rounded-2xl flex-1 shadow-lg hover:shadow-xl transition-shadow duration-300">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">{step.title}</h3>
                    <p className="text-gray-600 text-lg">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="contacto" className="py-20 bg-gradient-to-br from-teal-900 via-teal-800 to-blue-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="space-y-8">
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              ¿Necesitas una <span className="text-yellow-400">plataforma web escalable</span>?
            </h2>
            <p className="text-xl text-teal-100 max-w-3xl mx-auto">
              Construyamos juntos la aplicación web que llevará tu negocio al siguiente nivel. 
              Sin límites de crecimiento, sin compromisos en rendimiento.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <button
                onClick={onBack}
                className="inline-flex items-center px-8 py-4 bg-white text-teal-900 font-bold text-lg rounded-xl hover:bg-gray-100 transition-all duration-200 transform hover:scale-105 shadow-2xl"
              >
                Solicitar Cotización
                <ArrowRight className="ml-3 h-6 w-6" />
              </button>
              <button
                onClick={onBack}
                className="inline-flex items-center px-8 py-4 border-2 border-white/50 text-white font-semibold text-lg rounded-xl hover:bg-white/10 hover:border-white transition-all duration-200"
              >
                Hablar con un Arquitecto
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WebAppsPage;